//Global imports
import { CRow, CButton, CInputGroup, CFormInput } from '@coreui/react';
import React, { useEffect, useState } from 'react';
import { cilFolder } from '@coreui/icons';
import CIcon from '@coreui/icons-react';

//Component imports
import DocumentTemplateCard from '../DocumentTemplateCard';

//File imports
import Notification from '../../../../services/Notification/Notification';
import Logger from '../../../../services/Logger/Logger';
import { Language } from '../../../../language/English';
import { DocumentTemplateService } from '../../Api/DocumentTemplateService';
import Loader from '../../../../components/Layout/Loader';
import { DocumentTemplatePojo } from '../../Api/DocumentTemplatePojo';
import {
    DOCUMENT_TEMPLATE_STATUS_DELETED,
    DOCUMENT_TEMPLATE_STATUS_INACTIVE,
} from '../../Api/DocumentTemplateConstants';
import CreateTemplateModal from '../CreateTemplateModal';
import useInitialLoad from '../../../../services/Hooks/useInitialLoad';

const DocumentTemplateList: React.FC = () => {
    const notification = new Notification();
    const logger = new Logger();
    const documentTemplateService = new DocumentTemplateService();

    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [documentTemplates, setDocumentTemplates] = useState<
        DocumentTemplatePojo[][] | null
    >(null);
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
    const [editingTemplate, setEditingTemplate] =
        useState<DocumentTemplatePojo | null>(null);
    const [searchText, setSearchText] = useState<string>('');

    useInitialLoad({
        loaderCallback: () => {
            setShowLoader(true);
            fetchAllDocumentTemplates();
        },
    });

    const handleEdit = (template: DocumentTemplatePojo) => {
        setEditingTemplate(template);
        setShowCreateModal(true);
    };

    const handleModalClose = () => {
        setShowCreateModal(false);
        setEditingTemplate(null);
    };

    const handleSuccess = () => {
        fetchAllDocumentTemplates();
        handleModalClose();
    };

    const fetchAllDocumentTemplates = async () => {
        try {
            const response = await documentTemplateService.getAll();
            if (response && response.isSuccess) {
                const chunkedTemplates: any = [];
                for (
                    let i = 0;
                    i < response?.documentTemplates.length;
                    i += 2
                ) {
                    chunkedTemplates.push(
                        response?.documentTemplates.slice(i, i + 2),
                    );
                }
                setDocumentTemplates(chunkedTemplates);
            } else {
                notification.error(
                    Language.CANNOT_LOAD_DOCUMENT_TEMPLATES_ERROR,
                );
            }
            setShowLoader(false);
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            setShowLoader(false);
            logger.error(e);
        }
    };

    const changeDocumentStatus = async (
        documentTemplateId: number,
        status: number,
        documentTemplateName: string,
    ) => {
        setShowLoader(true);
        try {
            const documentTemplateService = new DocumentTemplateService();
            let response = await documentTemplateService.delete(
                documentTemplateId,
                status,
            );
            if (response) {
                if (status === DOCUMENT_TEMPLATE_STATUS_DELETED) {
                    notification.success(
                        documentTemplateName +
                            ' ' +
                            Language.DELETED_SUCCESSFULLY,
                    );
                } else {
                    let msg =
                        status === DOCUMENT_TEMPLATE_STATUS_INACTIVE
                            ? Language.INACTIVATED_SUCCESSFULLY
                            : Language.ACTIVATED_SUCCESSFULLY;
                    notification.success(documentTemplateName + ' ' + msg);
                }
                fetchAllDocumentTemplates();
            } else {
                notification.error(
                    Language.CANNOT_CHANGE_DOCUMENT_TEMPLATES_STATUS_ERROR,
                );
                setShowLoader(false);
            }
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            setShowLoader(false);
            logger.error(e);
        }
    };

    const filteredTemplates = documentTemplates
        ?.map((row: DocumentTemplatePojo[]) =>
            row.filter((template: DocumentTemplatePojo) =>
                template.name.toLowerCase().includes(searchText.toLowerCase()),
            ),
        )
        .filter((row) => row.length > 0);

    return (
        <>
            {showLoader ? <Loader /> : null}

            <div className="d-flex justify-content-between align-items-center mb-4 search-box-container">
                {documentTemplates && documentTemplates?.length > 0 ? (
                    <CInputGroup className="w-50 search-box">
                        <CFormInput
                            placeholder={Language.SEARCH_TEMPLATES}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </CInputGroup>
                ) : (
                    <div></div>
                )}

                <CButton
                    color="primary"
                    onClick={() => setShowCreateModal(true)}
                >
                    {Language.CREATE_DOCUMENT}
                </CButton>
            </div>

            {filteredTemplates && filteredTemplates.length > 0 ? (
                <CRow>
                    {filteredTemplates.map((row: any, rowIndex) =>
                        row.map((template: DocumentTemplatePojo) => (
                            <DocumentTemplateCard
                                key={template.documentTemplateId}
                                documentTemplate={template}
                                deleteDocumentTemplate={() =>
                                    changeDocumentStatus(
                                        template.documentTemplateId,
                                        DOCUMENT_TEMPLATE_STATUS_DELETED,
                                        template.name,
                                    )
                                }
                                changeDocumentTemplateStatus={(
                                    status: number,
                                ) =>
                                    changeDocumentStatus(
                                        template.documentTemplateId,
                                        status,
                                        template.name,
                                    )
                                }
                                onEdit={handleEdit}
                            />
                        )),
                    )}
                </CRow>
            ) : documentTemplates &&
              Array.isArray(documentTemplates) &&
              documentTemplates.length === 0 ? (
                <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{ minHeight: '60vh' }}
                >
                    <CIcon
                        icon={cilFolder}
                        size="3xl"
                        className="mb-3 text-secondary"
                    />
                    <h4 className="mb-3 text-center text-secondary fs-6">
                        {Language.NO_DOCUMENT_TEMPLATES} <br />
                        {Language.CREATE_HERE}
                    </h4>
                    <CButton
                        color="primary"
                        onClick={() => setShowCreateModal(true)}
                    >
                        {Language.CREATE_DOCUMENT}
                    </CButton>
                </div>
            ) : null}

            {showCreateModal && (
                <CreateTemplateModal
                    visible={showCreateModal}
                    onClose={handleModalClose}
                    onSuccess={handleSuccess}
                    mode={editingTemplate ? 'edit' : 'create'}
                    templateData={editingTemplate}
                />
            )}
        </>
    );
};

export default DocumentTemplateList;
